import {
  Alert,
  AlertIcon,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Flex,
  Spinner,
  Tag,
  TagLabel,
  TagRightIcon,
  Text,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { MasterOfferDetailDrawer } from './MasterOfferDetailDrawer';
import { BodyOverride } from '@texas/components/shared/BodyOverride';
import { VerifyDialogWithFunction } from '@texas/components/shared/dialog/VerifyDialogWithFunction';
import { CopyOfferDrawer } from './CopyOfferDrawer';
import { AddBranchToOfferModal } from './AddBranchToOfferModal';
import { reactEvents } from '@bridge/reactEvents';
import { Icons } from '@texas/components/shared/Icons';
import { MakeOfferedOfferDrawer } from './MakeOfferedOfferDrawer';
import {
  OfferedOfferSelectionProvider,
  useOfferedOfferSelection,
} from './OfferedOfferSelectionContext';
import { OfferGroupSummaryCard } from './OfferGroupSummaryCard';
import { SentOfferedOffersCard } from './SentOfferedOffersCard';
import { OfferBranchCard } from './OfferBranchCard';
import { OfferedOfferDetailsDrawer } from './OfferedOfferDetailsDrawer';
import { OfferProvider, useOffer } from './OfferContext';
import { useEffect } from 'react';

interface Props {
  offerGroupId: number;
  variantId: number;
}

export function OfferGroupDetailPage({ offerGroupId, variantId }: Props) {
  return (
    <BodyOverride>
      <OfferedOfferSelectionProvider>
        <OfferProvider offerGroupId={offerGroupId} variantId={variantId}>
          <OfferGroupDetailPageInner />
        </OfferProvider>
      </OfferedOfferSelectionProvider>
    </BodyOverride>
  );
}

function OfferGroupDetailPageInner() {
  const { t } = useTranslation();
  const {
    offerGroupId,
    variantId,
    group,
    loading,
    error,
    groupOfferedOffers,
    offeredOffersLoading,
    refreshGroup,
    refreshOfferedOffers,
    editingMatchedInquiry,
    editingOfferId,
    isEditingOfferOpen,
    closeEditingOffer,
    copyingOfferId,
    isCopyingOfferOpen,
    closeCopyingOffer,
    selectedOfferedOfferId,
    isOfferedOfferOpen,
    openOfferedOffer,
    closeOfferedOffer,
    isRemoveCurrencyOpen,
    closeRemoveCurrency,
    removeSelectedCurrency,
    isAddBranchOpen,
    openAddBranch,
    closeAddBranch,
    isDefaultEditingCurrency,
  } = useOffer();
  const { openMakeOfferWithAllPrices } = useOfferedOfferSelection();

  useEffect(() => {
    if (!group) return;

    const label =
      groupOfferedOffers.length > 0
        ? `${group.articleItemCode} / ${
            groupOfferedOffers.reduce((latest, offeredOffer) =>
              latest.addedOn > offeredOffer.addedOn ? latest : offeredOffer,
            ).offerNumber
          }`
        : group.articleItemCode;

    reactEvents.entityVisited.dispatch({
      id: group.id,
      type: 'offer',
      label,
      navobj: { articleId: group.articleId, variantId },
    });
  }, [group, groupOfferedOffers, variantId]);

  function renderTierTags() {
    if (!group) return null;

    type TierEntry =
      | {
          kind: 'offer';
          quantity: number;
          id: number;
          archived: boolean | null;
          inInquiry: boolean;
          inTarget: boolean;
        }
      | { kind: 'missing-target'; quantity: number }
      | { kind: 'missing-inquiry'; quantity: number };

    const entries: TierEntry[] = [
      ...group.tiers.map((tier) => ({
        kind: 'offer' as const,
        quantity: tier.quantity,
        id: tier.id,
        archived: tier.archived,
        inInquiry: group.inquiryTierQuantities.includes(tier.quantity),
        inTarget: group.targetTierQuantities.includes(tier.quantity),
      })),
      ...group.targetTierQuantities
        .filter((q) => !group.tiers.some((t) => t.quantity === q))
        .map((q) => ({ kind: 'missing-target' as const, quantity: q })),
      ...group.inquiryTierQuantities
        .filter(
          (q) =>
            !group.tiers.some((t) => t.quantity === q) &&
            !group.targetTierQuantities.includes(q),
        )
        .map((q) => ({ kind: 'missing-inquiry' as const, quantity: q })),
    ].sort((a, b) => a.quantity - b.quantity);

    return entries.map((entry) => {
      if (entry.kind === 'offer') {
        const colorScheme = entry.archived
          ? 'gray'
          : entry.inInquiry
          ? 'green'
          : 'red';
        return (
          <Tag
            key={entry.id}
            colorScheme={colorScheme}
            opacity={entry.archived ? 0.4 : 1}
            textDecoration={entry.archived ? 'line-through' : undefined}
            size="sm"
          >
            <TagLabel>{entry.quantity}</TagLabel>
            {entry.inTarget && <TagRightIcon as={Icons.Target} />}
          </Tag>
        );
      }
      if (entry.kind === 'missing-target') {
        return (
          <Tag
            key={`target-${entry.quantity}`}
            colorScheme="gray"
            variant="outline"
            size="sm"
          >
            <TagLabel>{entry.quantity}</TagLabel>
            <TagRightIcon as={Icons.Target} />
          </Tag>
        );
      }
      return (
        <Tag
          key={`inquiry-${entry.quantity}`}
          colorScheme="gray"
          variant="outline"
          size="sm"
        >
          {entry.quantity} {t('offer.inquiryTierSuffix')}
        </Tag>
      );
    });
  }
  if (!group) return null;

  return (
    <Box p={4}>
      <Breadcrumb fontSize="sm" mb={4}>
        <BreadcrumbItem>
          <BreadcrumbLink
            cursor="pointer"
            onClick={() =>
              reactEvents.routingChanged.dispatch({
                to: 'variant.offers',
                navObj: { variantId },
              })
            }
          >
            {t('offer.offers')}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink>{t('offer.offer')}</BreadcrumbLink>
        </BreadcrumbItem>
        {loading && (
          <BreadcrumbItem>
            <Spinner size="xs" />
          </BreadcrumbItem>
        )}
      </Breadcrumb>
      <OfferGroupSummaryCard
        group={group}
        tierTags={renderTierTags()}
        loading={loading}
        error={error}
        onUpdated={refreshGroup}
        onMakeOffer={openMakeOfferWithAllPrices}
        t={t}
      />

      <SentOfferedOffersCard
        offeredOffers={groupOfferedOffers}
        loading={offeredOffersLoading}
        onOpenOfferedOffer={openOfferedOffer}
        t={t}
      />

      {group.branches.some((b) =>
        b.currencies.some((c) => c.missingCellCount > 0),
      ) && (
        <Alert status="warning" mb={2} borderRadius="md">
          <AlertIcon />
          {t('offer.incompleteCells')}
        </Alert>
      )}

      {group.missingBranches.length > 0 && (
        <Alert status="warning" mb={2} borderRadius="md">
          <AlertIcon />
          <Flex align="center" justify="space-between" flex={1} gap={4}>
            <Flex align="center" gap={2} wrap="wrap">
              <Text fontSize="sm" fontWeight="medium">
                {t('offer.missingBranches')}:
              </Text>
              {group.missingBranches.map((b) => (
                <Tag key={b.branchId} size="sm" colorScheme="orange">
                  {b.branchIdentifier}
                </Tag>
              ))}
            </Flex>
            <Button
              size="sm"
              variant="texas-light"
              flexShrink={0}
              onClick={openAddBranch}
            >
              {t('offer.addBranchesToOffer')}
            </Button>
          </Flex>
        </Alert>
      )}

      <Flex direction="column" gap={4}>
        {group.branches.map((branch) => (
          <OfferBranchCard key={branch.branchId} branch={branch} />
        ))}
      </Flex>

      <MasterOfferDetailDrawer
        offerId={editingOfferId}
        isOpen={isEditingOfferOpen}
        isDefaultCurrency={isDefaultEditingCurrency}
        matchedInquiry={editingMatchedInquiry}
        customerId={group.customerId}
        onClose={() => {
          closeEditingOffer();
          refreshGroup();
        }}
      />
      <CopyOfferDrawer
        sourceOfferId={copyingOfferId}
        isOpen={isCopyingOfferOpen}
        onCopied={() => {
          refreshGroup();
        }}
        onClose={closeCopyingOffer}
      />
      <AddBranchToOfferModal
        offerGroupId={offerGroupId}
        customerId={group.customerId}
        missingBranches={group.missingBranches}
        existingMatchedInquiryIds={group.branches
          .flatMap((b) => b.acceptedInquiries)
          .filter((i) => i.isMatched)
          .map((i) => i.inquiryId)}
        isOpen={isAddBranchOpen}
        onClose={closeAddBranch}
        onAdded={refreshGroup}
      />
      <MakeOfferedOfferDrawerHost
        onCreated={() => {
          refreshGroup();
          refreshOfferedOffers();
        }}
      />
      <OfferedOfferDetailsDrawer
        offeredOfferId={selectedOfferedOfferId}
        isOpen={isOfferedOfferOpen}
        onClose={closeOfferedOffer}
      />
      <VerifyDialogWithFunction
        headerTitle={t('offer.removeCurrency')}
        isOpen={isRemoveCurrencyOpen}
        onClose={closeRemoveCurrency}
        secondaryButtonTitle={t('general.cancel')}
        primaryButtonTitle={t('offer.removeCurrency')}
        toPerform={removeSelectedCurrency}
      >
        {t('offer.removeCurrencyConfirm')}
      </VerifyDialogWithFunction>
    </Box>
  );
}

function MakeOfferedOfferDrawerHost({ onCreated }: { onCreated: () => void }) {
  const { variantId } = useOffer();
  const {
    makeOfferTierPriceIds,
    isMakeOfferOpen,
    closeMakeOffer,
    clearSelection,
  } = useOfferedOfferSelection();

  return (
    <MakeOfferedOfferDrawer
      variantId={variantId}
      tierPriceIds={makeOfferTierPriceIds}
      isOpen={isMakeOfferOpen}
      onClose={closeMakeOffer}
      onCreated={() => {
        clearSelection();
        onCreated();
      }}
    />
  );
}
